import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueAgentTasksProvider} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {ArtifactsPanel} from '@github-ui/scribe-artifacts/ArtifactPanel'
import {ArtifactsPanelProvider} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {clsx} from 'clsx'

import {IssueViewerContextProvider} from '../contexts/IssueViewerContext'
import {RelayLiveUpdatesContext} from '../contexts/relay-live-updates-context'
import type {IssueViewerIssue$data} from './__generated__/IssueViewerIssue.graphql'
import {IssueViewerInternalFragment, type IssueViewerViewerFragment} from './IssueViewer'
import styles from './IssueViewer.module.css'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import type {OptionConfig} from './OptionConfig'
import {SecondaryViewLiveUpdates} from './SecondaryViewLiveUpdates'
import {useIssueViewerContainerQueryFix} from './use-issue-viewer-container-query-fix'
import {useIssueViewerNavigationBlocker} from './use-issue-viewer-navigation-blocker'

export type DataRouterIssueViewerProps = {
  issue: IssueViewerIssue$data
  /** The oldest timeline section selected alongside the issue. */
  initialBackAnchor?: string
  viewer: IssueViewerViewerFragment | null
  isRepoOwnerEnterpriseManaged?: boolean | null
  userNameDisplayConfiguration?: string | null
  /** The route's own page-query key, so live updates refresh the header and sidebar it renders. */
  routeQueryKey?: readonly unknown[]
  /** `?timeline_page=N`, for the crawler's isolated historical window. */
  timelinePage?: number
  optionConfig: OptionConfig
}

/**
 * The issue viewer as the Data Router route renders it: the same component tree the Relay route
 * mounts, reading the page query's payload directly instead of through a Relay fragment.
 *
 * No data here comes from Relay, but leaves below still commit mutations through it. The route
 * provides that environment rather than this component, so the store survives issue navigation. The
 * preheat context schedules TanStack tasks only.
 */
export function DataRouterIssueViewer({
  issue,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name
  const issueViewerContainerRef = useIssueViewerContainerQueryFix(owner, repo, issue.number)

  return (
    <div
      ref={issueViewerContainerRef}
      className={clsx(
        styles.issueViewerContainer,
        styles.mainContainer,
        isFeatureEnabled('issues_sticky_header') && styles.mainContainer_stickyHeader,
      )}
    >
      <RelayCachePreheatContextProvider mode="tanstack">
        <DataRouterIssueViewerContent
          issue={issue}
          initialBackAnchor={initialBackAnchor}
          viewer={viewer}
          isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
          userNameDisplayConfiguration={userNameDisplayConfiguration}
          routeQueryKey={routeQueryKey}
          timelinePage={timelinePage}
          optionConfig={optionConfig}
        />
      </RelayCachePreheatContextProvider>
    </div>
  )
}

/** Split from the component above so `useSecondaryQuery` runs under the preheat context. */
function DataRouterIssueViewerContent({
  issue,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name

  const [secondaryIssueData, hideClosedSubIssuesEnabled, liveUpdateChannels, initialNestedSubIssuesData] =
    useSecondaryQuery({
      owner,
      repo,
      number: issue.number,
    })

  return (
    <RelayLiveUpdatesContext value={false}>
      <SecondaryViewLiveUpdates
        channels={liveUpdateChannels}
        owner={owner}
        repo={repo}
        number={issue.number}
        issueId={secondaryIssueData?.id ?? undefined}
        routeQueryKey={routeQueryKey}
      />
      <IssueAgentTasksProvider owner={owner} repo={repo} issueNumber={issue.number}>
        <UserNameDisplayContextProvider value={userNameDisplayConfiguration ?? undefined}>
          <ArtifactsPanelProvider>
            <IssueViewerContextProvider>
              <DataRouterIssueViewerInternal
                issueFragment={null}
                issueData={issue}
                initialBackAnchor={initialBackAnchor}
                // The timeline and secondary view are backed by TanStack and updated through Alive.
                disableRelayLiveUpdates
                viewer={viewer}
                isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
                secondaryIssueData={secondaryIssueData ?? undefined}
                initialNestedSubIssuesData={initialNestedSubIssuesData}
                hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
                timelinePage={timelinePage}
                issueUpdatesChannel={liveUpdateChannels?.updated}
                routeQueryKey={routeQueryKey}
                optionConfig={optionConfig}
              />
            </IssueViewerContextProvider>
            <DataRouterArtifactsPanel />
          </ArtifactsPanelProvider>
        </UserNameDisplayContextProvider>
      </IssueAgentTasksProvider>
    </RelayLiveUpdatesContext>
  )
}

export function DataRouterArtifactsPanel() {
  return <ArtifactsPanel editable />
}

function DataRouterIssueViewerInternal(props: Parameters<typeof IssueViewerInternalFragment>[0]) {
  useIssueViewerNavigationBlocker()

  return <IssueViewerInternalFragment {...props} />
}
